import { useState } from "react";
import { Calendar, Clock, ArrowRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const blogPosts = [
  {
    id: "code-and-crucible",
    title: "The Code and the Crucible: How I Learned That Great Leadership Needs More Than Logic",
    excerpt: "For years I thought success was about having the right answers. I eventually discovered that leadership is about asking better questions...",
    author: "Deepa Prashanth",
    date: "2026-02-13",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
    content: `
## The Code and the Crucible: How I Learned That Great Leadership Needs More Than Logic

*"For years I thought success was about having the right answers. I eventually discovered that leadership is about asking better questions."*

### A World That Made Perfect Sense

For the first twenty-four years of my life, I believed the world worked like a giant Excel spreadsheet. If I studied hard, memorised the formulas, organised my time and followed the rules, success would naturally follow. School rewarded logic, structure and certainty, and I became someone who loved solving problems. I also believed something many of us were taught—that some people are logical while others are emotional. I wanted to be one of the logical ones. Emotions felt messy and unpredictable. If a problem appeared, you solved it.

Then life happened. And life, as it turns out, doesn't run on Python.

### The Day My Logic Failed

Early in my career, a project was slipping because a team member was exhausted. My instinct was to fix the problem. I opened a carefully colour-coded Gantt chart and explained how they could work more efficiently. I genuinely thought I was helping. Instead, they burst into tears.

That moment changed me. I hadn't been speaking to a project plan. I'd been speaking to a person. It was the first time I realised that being technically competent wasn't enough to lead people well. Curious to understand what had happened, I immersed myself in leadership, psychology and neuroscience. One insight stood out above everything else: our brains don't choose between thinking and feeling—they constantly integrate both. Emotions aren't a weakness or an interruption. They're information. The brain's emotional systems alert us to what matters, while the prefrontal cortex helps us pause, think clearly and choose how to respond. When we ignore emotions, we're making decisions with incomplete information. Great leadership isn't less logical; it's more complete.

### The Neuropsychology Epiphany: Emotions are Just Data

Desperate to solve this puzzle, I did what any logical person does: I started reading peer-reviewed research. That is when I stumbled upon neuropsychology, and my entire worldview shifted. I discovered that the brain isn't actually split into a rigid "cold logic left" and "emotional right" battleground. In reality, the brain is an interconnected data network. High EQ doesn't mean you stop being logical. It means your analytical brain actively monitors, interprets, and leverages emotional data to make better decisions. When you ignore emotions, you are literally deleting half the data fields in the room. Attempting to lead, parent, or manage with "pure logic" is like trying to navigate a city street while refusing to look at traffic lights because you think colours are "irrational."

### Where This Changed My Life

As a manager, I learned that people rarely perform at their best when they feel unheard. Sometimes listening for ten minutes achieves more than solving the problem in two.

As a leader, I discovered people don't follow spreadsheets—they follow people who create trust, communicate honestly and genuinely care.

As a parent, I learned that connection comes before correction. Children, like adults, need to feel understood before they're ready to listen.

As a friend, I realised not every conversation requires advice. Sometimes the greatest gift is simply being present.

### The Biggest Rewrite

The greatest transformation wasn't learning another framework. It was unlearning habits that no longer served me. Professional coaching created the space to slow down, reflect and replace judgement with curiosity. Over time I learned to pause before reacting, ask better questions and recognise that not every problem needs an immediate solution.

### Final Reflection

Today I still love spreadsheets, strategy and structured thinking. Those strengths remain part of who I am. What has changed is my understanding of people. **Logic helps us understand problems. Empathy helps us understand people. The most effective leaders learn to use both.**

If this story resonates with you, perhaps it's an invitation to pause and reflect. Sometimes our greatest growth doesn't come from finding better answers—it comes from asking better questions. If you're looking for that space to think differently, I'd love to have a coaching clarity conversation with you.
    `,
  },
  {
    id: "ai-executive-coaching",
    title: "Leadership in the AI Revolution: The Crucial Role of Executive Coaching",
    excerpt: "Artificial Intelligence (AI) is changing the way organisations work, create, and lead. While a lot of talk is about AI tools and faster work, the biggest challenge is about how leaders manage people, culture, and ethics...",
    author: "Deepa Prashanth",
    date: "2026-02-12",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1677442135136-760c813028c0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
    content: `
## Leadership in the AI Revolution: The Crucial Role of Executive Coaching

Artificial Intelligence (AI) is changing the way organisations work, create, and lead. While a lot of talk is about AI tools and faster work, the biggest challenge is about how leaders manage people, culture, and ethics. As AI becomes part of day-to-day business, leaders need to rethink how they make decisions and support their teams. This is where executive coaching helps. It's not enough for leaders to just know about AI technology. They also need to be flexible, understand emotions, think about what's right, and lead with clear purpose. Executive coaching guides leaders to handle all these as they face AI changes.

### AI Creates New Opportunities but Also Challenges

AI lets many more people contribute and create ideas, not just specialists. But this new freedom can clash with older ways how organisations operate. Leaders have to balance allowing new ideas and experiments with making sure the business runs smoothly. Executive coaching helps leaders develop skills to manage this balance, supporting their teams with understanding and patience.

### AI Becomes Part of the Team

Today, AI doesn't just help behind the scenes; it interacts with customers and shapes what people think about the company. Leaders need to treat AI tools like important team members that affect trust and reputation. Coaching helps leaders think about AI carefully and use it responsibly.

### Leading Through Change and Stress

Many big changes are happening all at once—new technology, new cultures, new rules, multigenerational teams, multicultural teams—and AI makes this faster. This can cause stress and tiredness for leaders and their teams. Good leaders don't pretend to know everything; instead, they accept uncertainty and create safe spaces where people feel supported. Executive coaching teaches leaders how to stay calm, focused, and understanding during tough times.

### New Ideas About Success and People

AI brings questions about how work is done, fairness, and impact on society. Old ways of measuring success don't always fit anymore. Executive coaching helps leaders look at success in terms of purpose and caring for people. As AI spreads knowledge across the organisation, leadership needs to be shared by many, not just top people. Coaching helps build confidence and teamwork so everyone can lead in their role.

### How Coaching and AI Work Together

In the future, coaching will combine AI tools and personal support. Coaches will use AI to get insights but will also focus on building trust and empathy with leaders. Coaching helps leaders change old habits and use new technologies wisely.

### Building Strong AI Leadership Skills

Building strong leadership for AI means practising new skills and learning continuously. Some ways to do this include:

**Paradox Pods:** Teams from different areas work together with AI to explore different ideas and viewpoints.

**Prompt Fluency Workshops:** Training sessions that teach people how to use AI effectively by asking the right questions.

**Bias Audits:** Checking AI systems for unfairness and fixing any hidden biases to make sure things are fair for everyone.

**Narrative Labs:** Creating stories and messages that reflect the organisation's values, using insights from AI.

**Centres of Excellence:** Special groups or places focused on developing leadership skills and making sure AI is used in an ethical way.

Executive coaching helps leaders put these ideas into practice and keep improving.

---

### Conclusion: AI Helps Leaders Shine, Not Replace Them

AI is changing business and leadership, but it doesn't take away leaders' roles—it actually makes them more important. The best leaders will be open to new ideas, share power, and work with their teams openly. Executive coaching makes sure AI is used to support people and values, helping leaders manage tough challenges with care and confidence.
    `,
  },
  {
    id: "parenting-coaching",
    title: "How Parenting Can Be Like Coaching: Nurturing Kids' Wellbeing",
    excerpt: "One evening, after a long day at school, my 15-year-old daughter came home in tears. She had just received a disappointing grade on a maths test. Rather than jumping straight into reassurances, I paused to reflect on how a coach might handle a similar situation...",
    author: "Deepa Prashanth",
    date: "2026-02-12",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1476703993599-0035a21b17a9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600",
    content: `
## How Parenting Can Be Like Coaching: Nurturing Kids' Wellbeing

One evening, after a long day at school, my 15-year-old daughter, Mia, came home in tears. She had just received a disappointing grade on a maths test and felt like she had let everyone down. As I sat down beside her, I could see the weight of her disappointment. Rather than jumping straight into reassurances, I paused to reflect on how a coach might handle a similar situation.

I recalled a time when her soccer coach had faced a similar challenge with the team. Instead of focusing solely on the loss, the coach gathered the players and encouraged them to discuss what went wrong, what they could learn, and how they could improve moving forward. Inspired by that memory, I asked Mia how she felt about the test and what she thought she could do differently next time.

As we talked, I saw her begin to shift from feeling defeated to thinking about solutions. We set a small goal together: she would practice maths for just 15 minutes a day. By the end of our conversation, she was smiling again, feeling empowered to take charge of her learning, just as a player would after a motivating coaching session.

This moment made me realise how closely parenting can resemble coaching. By adopting a coaching mindset, I was not just comforting her but actively helping her build resilience and a growth mindset—skills that would serve her well beyond the classroom.

### The Importance of Parenting and Coaching in a Child's Development

Parenting and coaching play crucial roles in shaping a child's overall development. Through love, support, and guidance, parents instil values, build self-esteem, and nurture social skills. A positive parenting approach fosters a sense of security, enabling children to explore their world and develop resilience in the face of challenges.

On the other hand, coaching provides a structured framework for skill development and goal achievement. Coaches focus on enhancing specific abilities, whether in academics, sports, or personal growth. They encourage children to set realistic goals, push their limits, and learn from failures. This guidance helps children develop a growth mindset, teaching them that effort and perseverance lead to improvement.

When combined, effective parenting and coaching create a holistic approach to development. Children benefit from emotional support while also receiving the tools they need to navigate challenges and achieve their aspirations. This dual influence equips them with the resilience, confidence, and skills necessary for lifelong success.

### Parallels Between Parenting and Coaching

**Setting Goals and Establishing Agreements**
Just like coaches help athletes set realistic and achievable goals, parents can work with their children to establish personal goals, whether academic, social, or emotional. Age-appropriate goals contribute to a child's sense of accomplishment.

**Providing Support and Encouragement**
Emotional support is crucial in both coaching and parenting. Fostering resilience in children means celebrating small victories and helping them learn from setbacks.

**Communicating Effectively**
Effective communication is crucial in coaching and parenting. Active listening and open dialogue, along with asking open-ended questions, encourage children to express their feelings and thoughts.

**Modelling Behaviour**
Coaches lead by example, and parents can do the same. Modelling healthy behaviours, such as stress management and problem-solving, matters because children often imitate their parents—positive behaviour leads to healthier habits.

**Building a Team Environment**
Involving children in family decisions fosters a sense of belonging and responsibility. Creating a supportive home environment where children feel safe to express themselves is essential.

**Prioritizing Presence**
Stop several times each day and take in the moment. If you're breathing, and your child is alive, then you are CRUSHING it. If you are present, asking simple questions you don't know the answer to, supporting what's important, then you are CRUSHING IT.

**Regulating Your Nervous System**
As a parent to children of any age who have epic tantrums, remember that crying is a stress release. It's the child's only outlet of frustration, and it's in their best interest to let it out of their system. It's our job to help them learn to navigate it and keep a loving, calm energy.

---

In conclusion, I encourage all readers to reflect on your role as a parent and how a coaching mindset can enhance and enrich the relationship goals with your child. Wishing you all the best in your parenting journey—both for parents and coaches alike!
    `,
  },
];

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (selectedPost) {
    return (
      <section id="blog" data-testid="blog-section" className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Button
            variant="ghost"
            onClick={() => setSelectedPost(null)}
            className="mb-8"
            data-testid="back-to-blog"
          >
            ← Back to Blog
          </Button>
          
          <article className="prose prose-lg max-w-none">
            <img
              src={selectedPost.image}
              alt={selectedPost.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl mb-8"
            />
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>{selectedPost.author}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(selectedPost.date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{selectedPost.readTime}</span>
              </div>
            </div>
            
            <div 
              className="text-foreground leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ 
                __html: selectedPost.content
                  .replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold mt-8 mb-4 font-serif">$1</h2>')
                  .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-6 mb-3">$1</h3>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n\n/g, '</p><p class="mb-4">')
                  .replace(/---/g, '<hr class="my-8 border-border" />')
              }}
            />
            
            {/* Author Bio */}
            <div className="mt-12 p-6 bg-muted/50 rounded-xl">
              <h4 className="font-semibold mb-2">About the Author</h4>
              <p className="text-sm text-muted-foreground">
                Deepa has 20 years of experience across Strategic HR, Project Management and enabling Process improvement. 
                She is currently on the path to ICF accreditation and truly believes that becoming a coach can be a deeply 
                rewarding experience, providing the opportunity to inspire, lead, and impact the lives of others.
              </p>
            </div>
          </article>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" data-testid="blog-section" className="py-20 md:py-32 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
            Insights
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Blog & Articles
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Thoughts on leadership, coaching, personal growth, and navigating 
            life's challenges with confidence.
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Card
              key={post.id}
              data-testid={`blog-card-${post.id}`}
              className="overflow-hidden border-border/50 bg-background hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedPost(post)}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform hover:scale-105"
                />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(post.date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
                
                <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                  {post.title}
                </h3>
                
                <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center text-primary text-sm font-medium">
                  Read More
                  <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
